import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Scale, ShieldCheck } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import RecordTag from "@/components/RecordTag";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { publicApi } from "@/lib/api";

export default function ComplaintPage({ session, onLogout }) {
  const [submittedId, setSubmittedId] = useState("");
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      role: "Mandant",
      subject: "",
      member_reference: "",
      description: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const response = await publicApi.submitComplaint(values);
      setSubmittedId(response.id);
      toast.success("Ihre Eingabe wurde an die Beschwerdestelle übergeben.");
      reset();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Die Eingabe konnte nicht gespeichert werden.");
    }
  };

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Beschwerdestelle"
      pageDescription="Beschwerdestelle und Eingabestrecke für Mitglieder und Mandanten bei REEXPERTS."
    >
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-10 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-6 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="complaint-info-panel">
            <Badge className="w-fit rounded-full bg-amber-50 text-amber-800" data-testid="complaint-badge">
              <Scale className="mr-2 h-4 w-4" /> Freie Schiedstelle / Beschwerdestelle
            </Badge>
            <h1 className="font-serif text-3xl tracking-tight text-slate-950" data-testid="complaint-heading">
              Geordnete, dokumentierte und sachliche Klärung von Anliegen.
            </h1>
            <p className="text-base leading-relaxed text-slate-600" data-testid="complaint-description">
              Für Mitglieder und deren Mandanten steht eine geordnete Eingabestrecke bereit. Hinweise werden intern als Beschwerdevorgang erfasst und für die weitere Bearbeitung vorbereitet.
            </p>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="complaint-process-box">
              <p className="font-semibold text-slate-950">Verfahrensgang</p>
              <ol className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
                <li>1. Eingang der Beschwerde mit Referenz auf Mitglied oder Vorgang</li>
                <li>2. Vorsortierung und interne Vorgangsanlage im System</li>
                <li>3. Nachverfolgung im Verbandsbereich mit dokumentiertem Status</li>
              </ol>
            </div>
            {submittedId && (
              <div className="rounded-3xl border border-blue-200 bg-blue-50 p-5 text-blue-800" data-testid="complaint-success-box">
                <p>Vorgang erfolgreich angelegt:</p>
                <div className="mt-3">
                  <RecordTag value={submittedId} testId="complaint-success-record-tag" />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 rounded-[2rem] border border-slate-200 bg-slate-950 p-8 text-white shadow-sm" data-testid="complaint-form-panel">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-400" data-testid="complaint-form-kicker">Eingabeformular</p>
              <h2 className="mt-3 font-serif text-[2rem] tracking-tight" data-testid="complaint-form-heading">Sachverhalt mitteilen</h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium">Name</label>
                <Input {...register("full_name", { required: true })} className="border-white/10 bg-white/5 text-white" data-testid="complaint-full-name-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">E-Mail</label>
                <Input type="email" {...register("email", { required: true })} className="border-white/10 bg-white/5 text-white" data-testid="complaint-email-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Rolle</label>
                <select {...register("role")} className="h-10 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white" data-testid="complaint-role-select">
                  <option className="text-slate-900" value="Mandant">Mandant</option>
                  <option className="text-slate-900" value="Mitglied">Mitglied</option>
                  <option className="text-slate-900" value="Interessent">Interessent</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">REEXPERTS-ID / Referenz (optional)</label>
                <Input {...register("member_reference")} className="border-white/10 bg-white/5 text-white" data-testid="complaint-member-reference-input" />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium">Betreff</label>
                <Input {...register("subject", { required: true })} className="border-white/10 bg-white/5 text-white" data-testid="complaint-subject-input" />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium">Beschreibung</label>
                <Textarea {...register("description", { required: true })} className="min-h-[180px] border-white/10 bg-white/5 text-white" data-testid="complaint-description-textarea" />
              </div>
            </div>

            <Button className="rounded-full bg-white text-slate-950 hover:bg-slate-200" data-testid="complaint-submit-button">
              <ShieldCheck className="mr-2 h-4 w-4" /> Beschwerde absenden
            </Button>
          </form>
        </motion.div>
      </div>
    </MainLayout>
  );
}
